import { useEffect } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker, Polyline, Circle, Tooltip, LayersControl, LayerGroup, useMap, useMapEvents } from "react-leaflet";
import { POM_CENTER } from "@/constants/places";

const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services";
const ESRI_ATTR = "Imagery &copy; Esri, Maxar, Earthstar Geographics";

const pin = (color, letter) =>
  L.divIcon({
    className: "",
    html: `<div class="ct-pin" style="background:${color}"><span>${letter}</span></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
  });

const PICK = pin("#1E40AF", "A");
const DROP = pin("#D97706", "B");
const CAR = (s) => `<svg viewBox="0 0 24 24" width="${s}" height="${s}" fill="none" stroke="#0B192C" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2h4"/><path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8"/><path d="M7 14h.01"/><path d="M17 14h.01"/><rect width="18" height="8" x="3" y="10" rx="2"/><path d="M5 18v2"/><path d="M19 18v2"/></svg>`;
const TAXI = L.divIcon({ className: "", html: `<div class="ct-taxi">${CAR(18)}</div>`, iconSize: [40, 40], iconAnchor: [20, 20] });
const NEAR = L.divIcon({ className: "", html: `<div class="ct-taxi-sm">${CAR(14)}</div>`, iconSize: [30, 30], iconAnchor: [15, 15] });
const NEAR_SEL = L.divIcon({ className: "", html: `<div class="ct-taxi-sm sel">${CAR(14)}</div>`, iconSize: [30, 30], iconAnchor: [15, 15] });
const NEAR_OFF = L.divIcon({ className: "", html: `<div class="ct-taxi-sm off">${CAR(14)}</div>`, iconSize: [30, 30], iconAnchor: [15, 15] });
const ME = L.divIcon({ className: "", html: '<div class="ct-me"></div>', iconSize: [20, 20], iconAnchor: [10, 10] });
const BASE = L.divIcon({ className: "", html: '<div class="ct-base">HQ</div>', iconSize: [38, 38], iconAnchor: [19, 19] });

const Follow = ({ pos }) => {
  const map = useMap();
  useEffect(() => {
    if (pos) map.panTo([pos.lat, pos.lng], { animate: true });
  }, [map, pos]);
  return null;
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) {
      if (onPick) onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const FitBounds = ({ pickup, dropoff, taxi, fitPoints, fitKey }) => {
  const map = useMap();
  const hasTaxi = Boolean(taxi);
  const key = [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, hasTaxi, fitKey].join(",");
  useEffect(() => {
    const pts = fitPoints || [pickup, dropoff, taxi].filter(Boolean).map((p) => [p.lat, p.lng]);
    if (pts.length > 1) map.fitBounds(pts, { padding: [50, 50], maxZoom: 15 });
    else if (pts.length === 1) map.setView(pts[0], 14);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
};

const NearbyMarkers = ({ drivers, onDriverClick, selectedDriverId }) =>
  drivers.map((d) => (
    <Marker
      key={d.driver_id}
      position={[d.lat, d.lng]}
      icon={d.driver_id === selectedDriverId ? NEAR_SEL : d.live === false ? NEAR_OFF : NEAR}
      eventHandlers={onDriverClick ? { click: () => onDriverClick(d) } : undefined}
    >
      <Tooltip direction="top" offset={[0, -14]}>{d.taxi_no ? `#${d.taxi_no} · ` : ""}{d.name}{d.km != null ? ` · ${d.km} km` : ""}</Tooltip>
    </Marker>
  ));

export const MapPicker = ({
  pickup, dropoff, taxi, onPick, className = "h-[420px]", route, routeColor = "#4F46E5", drivers = [], onDriverClick,
  selectedDriverId, passenger, base, track, follow, fitPoints, fitKey, testId = "booking-map",
}) => (
  <div data-testid={testId} className={`overflow-hidden rounded-2xl border border-blue-100 shadow-xl shadow-blue-900/5 ${className}`}>
    <MapContainer center={POM_CENTER} zoom={13} scrollWheelZoom className="h-full w-full">
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="Satellite">
          <LayerGroup>
            <TileLayer attribution={ESRI_ATTR} url={`${ESRI}/World_Imagery/MapServer/tile/{z}/{y}/{x}`} maxZoom={19} />
            <TileLayer url={`${ESRI}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}`} maxZoom={19} />
            <TileLayer url={`${ESRI}/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}`} maxZoom={19} opacity={0.7} />
          </LayerGroup>
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Street map">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
      </LayersControl>
      <ClickHandler onPick={onPick} />
      <FitBounds pickup={pickup} dropoff={dropoff} taxi={follow ? null : taxi} fitPoints={fitPoints} fitKey={fitKey} />
      {follow && taxi && <Follow pos={taxi} />}
      {base && (
        <>
          <Circle center={[base.lat, base.lng]} radius={base.radius_m || 200} pathOptions={{ color: "#FBBF24", weight: 2, fillOpacity: 0.12 }} />
          <Marker position={[base.lat, base.lng]} icon={BASE}><Tooltip direction="top" offset={[0, -18]}>{base.name}</Tooltip></Marker>
        </>
      )}
      {track && track.length > 1 && <Polyline positions={track} pathOptions={{ color: "#22D3EE", weight: 4, opacity: 0.9 }} />}
      {route && route.length > 1 && (
        <>
          <Polyline positions={route} pathOptions={{ color: "#ffffff", weight: 9, opacity: 0.85 }} />
          <Polyline positions={route} pathOptions={{ color: routeColor, weight: 6 }} />
        </>
      )}
      {!route && pickup && dropoff && (
        <Polyline positions={[[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]]} pathOptions={{ color: "#FBBF24", weight: 4, dashArray: "8 8" }} />
      )}
      <NearbyMarkers drivers={drivers} onDriverClick={onDriverClick} selectedDriverId={selectedDriverId} />
      {passenger && <Marker position={[passenger.lat, passenger.lng]} icon={ME} zIndexOffset={900}><Tooltip direction="top">Passenger</Tooltip></Marker>}
      {taxi && <Marker position={[taxi.lat, taxi.lng]} icon={TAXI} zIndexOffset={1000} />}
      {pickup && <Marker position={[pickup.lat, pickup.lng]} icon={PICK} />}
      {dropoff && <Marker position={[dropoff.lat, dropoff.lng]} icon={DROP} />}
    </MapContainer>
  </div>
);
